/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { FADE_UP_PILL } from '@/lib/motionPresets';
import { metaForStep, TONE_STYLES } from '@/components/onboarding/cta/nextStepMeta';
import { useOnboardingCta } from '@/components/onboarding/cta/useOnboardingCta';
import { ProgressRing } from '@/components/onboarding/cta/ProgressRing';

export { OnboardingBanner } from '@/components/onboarding/cta/OnboardingBanner';

const STORAGE_KEY = 'sc_onb_dismissed';

/**
 * Onboarding CTA badge — appears only for healthcare professionals whose
 * onboarding is incomplete. Shown as a compact pill in the TopBar to increase
 * completion rate without being intrusive. Hidden on /onboarding itself.
 */
const OnboardingCTA = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { visible, status, dismiss } = useOnboardingCta(STORAGE_KEY, {
    hideWhen: location.pathname.startsWith('/onboarding'),
  });
  if (!visible) return null;

  const meta = metaForStep(status.next_step);
  const go = () => navigate('/onboarding');
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
  };
  const onDismiss = (e) => { e.stopPropagation(); dismiss(); };

  return (
    <AnimatePresence>
      <motion.div
        {...FADE_UP_PILL}
        role="button"
        tabIndex={0}
        onClick={go}
        onKeyDown={onKeyDown}
        data-testid="onboarding-cta"
        aria-label={`${meta.label} — ${meta.detail}`}
        className={`hidden sm:inline-flex items-center gap-2 pl-2 pr-1 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${TONE_STYLES[meta.tone]}`}
      >
        <ProgressRing percent={meta.percent} Icon={meta.Icon} />
        <span className="flex items-center gap-1.5">
          <span className="font-semibold">{meta.label}</span>
          <span className="hidden md:inline text-[11px] opacity-80">· {meta.percent}%</span>
          <span className="hidden lg:inline text-[11px] opacity-80">· {meta.detail}</span>
        </span>
        <span className="inline-flex items-center gap-0.5 ml-0.5">
          <ArrowRight className="h-3 w-3 opacity-70" aria-hidden="true" />
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss for this session"
            data-testid="onboarding-cta-dismiss"
            className="p-0.5 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-current"
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        </span>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingCTA;
